import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { useRef } from "react";
import * as yup from "yup";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { asanaKeys } from "../../../../api/queryKeys";
import FormTextField from "../../../../components/forms/FormTextField";
import LoadingIcon from "../../../../components/LoadingIcon";
import RedAsterisk from "../../../../components/RedAsterisk";
import { getControlEntityIds } from "../../../posture/controls/components/EntitiesSection/utils";
import AsanaAssignee from "./fields/AsanaAssignee";
import AsanaIncludeEntities from "./fields/AsanaIncludeEntities";
import AsanaNotes from "./fields/AsanaNotes";
import AsanaProjects from "./fields/AsanaProjects";

const ASANA_FORM_WIDTH = "450px";
const ASANA_FORM_HEIGHT = "720px";

const AsanaTaskCreationModal = ({
  open,
  onClose,
  onSubmit,
  isLoading,
  fullControl,
}) => {
  const formRef = useRef(null);

  const colors = useColors();
  const { getAsanaProjects, getAsanaUsers } = useAPI();

  const defaultTaskName = fullControl?.name;
  const defaultNotes =
    fullControl?.name_for_gap_state +
    "\n\n*Remediation Steps*\n" +
    fullControl?.remediation.remediation_steps_summary
      .map((step, index) => `${index + 1}. ${step}`)
      .join("\n");

  const handleSubmit = (values) => {
    onSubmit(values);
  };

  const formInitialValues = {
    taskName: defaultTaskName || "",
    projects: [],
    assignee: null,
    notes: defaultNotes || "",
    includeEntities: getControlEntityIds(fullControl)?.length > 0,
  };

  const formValidationSchema = yup.object().shape({
    taskName: yup.string().required("Task name is required"),
    projects: yup
      .array()
      .of(yup.object().required())
      .min(1, "At least one project is required"),
    assignee: yup.object().required("Assignee is required"),
  });

  // Project options
  const { data: projectOptions, isSuccess: projectOptionsIsSuccess } = useQuery(
    {
      queryKey: asanaKeys.asanaProjects(),
      queryFn: getAsanaProjects,
      select: (response) => response.data,
    }
  );

  // Assignee options
  const { data: assigneeOptions, isSuccess: assigneeOptionsIsSuccess } =
    useQuery({
      queryKey: asanaKeys.asanaUsers(),
      queryFn: () => getAsanaUsers(),
      select: (response) => response.data,
    });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: colors.jira_modal_bg,
        },
      }}
    >
      <DialogTitle sx={{ paddingBottom: 0 }}>
        <Typography variant="subtitle1" color={colors.text_primary}>
          Create Asana Task
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          "&:first-of-type": {
            paddingTop: 0,
          },
        }}
      >
        {/* This Box keeps the sub-titles on top when scrolling down the modal */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            backgroundColor: colors.jira_modal_bg,
            zIndex: 1,
            padding: "16px 0",
          }}
        >
          <Box sx={{ marginBottom: "16px" }}>
            <Typography variant="body2" color={colors.text_secondary}>
              Creating a task for Control #{fullControl?.control_id}
            </Typography>
          </Box>
          <Box
            sx={{
              marginBottom: "12px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <Typography variant="caption" color={colors.text_secondary}>
              Required fields are marked with an asterisk{" "}
            </Typography>
            <RedAsterisk />
          </Box>
        </Box>

        <Box sx={{ width: ASANA_FORM_WIDTH, height: ASANA_FORM_HEIGHT }}>
          <Formik
            innerRef={formRef}
            onSubmit={handleSubmit}
            initialValues={formInitialValues}
            validationSchema={formValidationSchema}
            enableReinitialize
          >
            {({ handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {/* Task name field */}
                  <FormTextField
                    label="Task name"
                    fieldName="taskName"
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor:
                          colors.textfield_outlined_enabled_auto_generated_bg,
                      },
                    }}
                  />
                  <AsanaAssignee
                    options={assigneeOptions}
                    showOptions={assigneeOptionsIsSuccess}
                  />
                  <AsanaProjects
                    options={projectOptions}
                    showOptions={projectOptionsIsSuccess}
                    required={true}
                  />
                  <AsanaNotes />
                  <AsanaIncludeEntities />
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </DialogContent>

      <Box
        sx={{
          justifyContent: "center",
          display: "flex",
          margin: "0 24px 12px",
        }}
      >
        <DialogActions>
          {/* Styling this button in-place until we have a button-text-inherit style button */}
          <Button
            variant="text"
            onClick={onClose}
            disabled={isLoading}
            sx={{
              backgroundColor: colors.button_text_inherit_enabled_bg,
              color: colors.button_text_inherit_enabled_text,
              borderRadius: "10px",
              minWidth: "64px",
              padding: "7px 14.5px",
              "&:hover": {
                backgroundColor: colors.button_text_inherit_hovered_bg,
              },
            }}
          >
            <Typography variant="overline">Cancel</Typography>
          </Button>
          {/* Styling this button in-place until we have a button-contained-primary style button */}
          <Button
            variant="contained"
            onClick={() => {
              formRef.current?.submitForm();
            }}
            disabled={isLoading}
            startIcon={isLoading ? <LoadingIcon size={16} /> : null}
            sx={{
              backgroundColor: colors.button_contained_primary_enabled_bg,
              color: colors.button_contained_primary_enabled_text,
              borderRadius: "10px",
              minWidth: "64px",
              padding: "7px 14.5px",
              "&:hover": {
                backgroundColor: colors.button_contained_primary_hovered_bg,
              },
            }}
          >
            <Typography variant="overline">
              {isLoading ? "Creating..." : "Create"}
            </Typography>
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default AsanaTaskCreationModal;
